(() => {
  const registrations = new Map();
  const instances = new Map();
  const editor = Boolean(window.Shopify?.designMode);

  const matchesIn = (scope, selector) => {
    const nodes = [];
    if (scope instanceof Element && scope.matches(selector)) nodes.push(scope);
    if (scope.querySelectorAll) nodes.push(...scope.querySelectorAll(selector));
    return [...new Set(nodes)];
  };

  const mount = (registration, root) => {
    let mounted = instances.get(registration.name);
    if (!mounted) {
      mounted = new Map();
      instances.set(registration.name, mounted);
    }
    if (mounted.has(root)) return;
    const controller = new AbortController();
    try {
      const teardown = registration.setup(root, {
        signal: controller.signal,
        editor,
        reducedMotion: window.matchMedia('(prefers-reduced-motion: reduce)').matches
      });
      mounted.set(root, () => {
        controller.abort();
        if (typeof teardown === 'function') teardown();
      });
    } catch (error) {
      controller.abort();
      console.error(`[Boger Pets] ${registration.name} failed to initialise`, error);
    }
  };

  const init = (scope = document) => {
    registrations.forEach(registration => {
      matchesIn(scope, registration.selector).forEach(root => mount(registration, root));
    });
  };

  const unload = scope => {
    instances.forEach(mounted => {
      [...mounted.entries()].forEach(([root, teardown]) => {
        if (root === scope || scope.contains?.(root)) {
          teardown();
          mounted.delete(root);
        }
      });
    });
  };

  const revealEditorTarget = target => {
    let hidden = target.closest?.('[hidden]');
    while (hidden) {
      hidden.hidden = false;
      hidden.classList.remove('is-hidden');
      hidden = hidden.parentElement?.closest?.('[hidden]');
    }
    const interactive = target.closest?.('[data-v2-journey-tab], [data-v2-catalogue-tab], [data-v2-role-tab], [data-story-card]');
    interactive?.click();
    if (target instanceof HTMLOptionElement) {
      target.selected = true;
      target.parentElement?.focus();
    }
    const details = target.closest?.('details');
    if (details) details.open = true;
    target.closest?.('[data-bp-section]')?.classList.add('is-editor-selected');
  };

  const register = (name, selector, setup) => {
    registrations.set(name, { name, selector, setup });
    if (document.readyState !== 'loading') init(document);
  };

  window.BogerSections = { register, init, unload };

  register('section-motion', '[data-bp-section]', (root, context) => {
    if (context.editor || context.reducedMotion || root.dataset.motion === 'none') {
      root.classList.add('bp-motion-visible');
      return;
    }
    root.classList.add('bp-motion-pending');
    if (!('IntersectionObserver' in window)) {
      root.classList.add('bp-motion-visible');
      return;
    }
    const observer = new IntersectionObserver(entries => {
      entries.forEach(entry => {
        if (!entry.isIntersecting) return;
        root.classList.add('bp-motion-visible');
        observer.disconnect();
      });
    }, { threshold: 0.12, rootMargin: '0px 0px -7% 0px' });
    observer.observe(root);
    return () => observer.disconnect();
  });

  document.addEventListener('DOMContentLoaded', () => init(document), { once: true });
  document.addEventListener('shopify:section:load', event => init(event.target));
  document.addEventListener('shopify:section:unload', event => unload(event.target));
  document.addEventListener('shopify:section:select', event => {
    init(event.target);
    revealEditorTarget(event.target);
  });
  document.addEventListener('shopify:section:deselect', event => {
    event.target.querySelector?.('[data-bp-section]')?.classList.remove('is-editor-selected');
  });
  document.addEventListener('shopify:block:select', event => {
    const section = event.target.closest?.('.shopify-section') || event.target;
    init(section);
    revealEditorTarget(event.target);
    event.target.scrollIntoView?.({ block: 'center', behavior: 'auto' });
  });
})();
